import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from 'views/components';
import {useNavigate, translate, AddCaregiver} from 'views/module';

import JobFeed from './job_feed/JobFeed';

import JOB_SOURCES from '../job_source';

import backend from 'utils/backend';

const JobView = (props) => {
  const {jobRn, statusProgressWrap: progressWrap, requester} = props;
  const jobSource = props.jobSource || JOB_SOURCES.LEGACY;

  const navigate = useNavigate();

  const [refresh, setRefresh] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);

  const [jobData, setJob] = React.useState(null);
  const [client, setClient] = React.useState(null);
  const [applications, setApplications] = React.useState([]);
  const [caregivers, setCaregivers] = React.useState([]);
  const [caregiversLoading, setCaregiversLoading] = React.useState(false);
  const [caregiverNameFilter, setCaregiverNameFilter] = React.useState('');
  const [debouncedCaregiverNameFilter, setDebouncedCaregiverNameFilter] = React.useState('');
  const [languagesFilter, setLanguagesFilter] = React.useState([]);
  const [languagesFilterOp, setLanguagesFilterOp] = React.useState('OR');
  const [caregiverStatusFilter, setCaregiverStatusFilter] = React.useState(['ACTIVE']);
  const [radius, setRadius] = React.useState(10);
  const [acceptTarget, setAcceptTarget] = React.useState(null);
  const [acceptWarning, setAcceptWarning] = React.useState(null);
  const [jobMessages, setJobMessages] = React.useState({});
  const [latestAnnouncementMessage, setLatestAnnouncementMessage] = React.useState(null);
  const [jobMessagesLastEvaluatedKey, setJobMessagesLastEvaluatedKey] = React.useState(null);
  const [jobMessagesLoading, setJobMessagesLoading] = React.useState(false);

  const [feedTab, setFeedTab] = React.useState('INFORMATION');
  const requesterRn = requester.userRn;
  const clientRn = jobData?.client?.clientRn;
  const clientName = client?.clientDetail?.clientName;
  const jobLatitude = jobData?.location?.geoCode?.latitude;
  const jobLongitude = jobData?.location?.geoCode?.longitude;

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedCaregiverNameFilter(caregiverNameFilter.trim());
    }, 400);

    return () => clearTimeout(timeout);
  }, [caregiverNameFilter]);

  const handleSetRadius = (r) => {
    setCaregiverNameFilter('');
    setDebouncedCaregiverNameFilter('');

    const nearbyCaregiverSettings = localStorage.getItem('job/nearbyCaregiversSettings');
    const parsedNearbyCaregiverSettings = JSON.parse(nearbyCaregiverSettings);

    if (!nearbyCaregiverSettings || Object.keys(parsedNearbyCaregiverSettings).length === 0) {
      localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify({
        filters: [],
        radiusPreference: {[jobRn]: r},
      }));
      return setRadius(r);
    }

    parsedNearbyCaregiverSettings.radiusPreference[jobRn] = r;
    localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify(parsedNearbyCaregiverSettings));
    setRadius(r);
  };

  const handleSetCaregiverStatusFilter = (caregiverStatusFilter) => {
    const nearbyCaregiverSettings = localStorage.getItem('job/nearbyCaregiversSettings');
    const parsedNearbyCaregiverSettings = JSON.parse(nearbyCaregiverSettings);

    if (!nearbyCaregiverSettings || Object.keys(parsedNearbyCaregiverSettings).length === 0) {
      localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify({
        filters: caregiverStatusFilter.length > 0 ? [{key: 'status', value: caregiverStatusFilter}] : [],
        radiusPreference: {},
      }));
      return setCaregiverStatusFilter(caregiverStatusFilter);
    }

    parsedNearbyCaregiverSettings.filters = parsedNearbyCaregiverSettings.filters.filter((filter) => filter.key !== 'status');
    if (caregiverStatusFilter.length > 0) parsedNearbyCaregiverSettings.filters.push({key: 'status', value: caregiverStatusFilter});

    localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify(parsedNearbyCaregiverSettings));
    setCaregiverStatusFilter(caregiverStatusFilter);
  };

  const handleSetLanguagesFilter = (languagesFilter) => {
    const nearbyCaregiverSettings = localStorage.getItem('job/nearbyCaregiversSettings');
    const parsedNearbyCaregiverSettings = JSON.parse(nearbyCaregiverSettings);

    if (!nearbyCaregiverSettings || Object.keys(parsedNearbyCaregiverSettings).length === 0) {
      localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify({
        filters: languagesFilter.length > 0 ? [{key: 'languages', value: languagesFilter}] : [],
        languagesFilterOp,
        radiusPreference: {},
      }));
      return setLanguagesFilter(languagesFilter);
    }

    parsedNearbyCaregiverSettings.filters = parsedNearbyCaregiverSettings.filters.filter((filter) => filter.key !== 'languages');
    if (languagesFilter.length > 0) parsedNearbyCaregiverSettings.filters.push({key: 'languages', value: languagesFilter});
    parsedNearbyCaregiverSettings.languagesFilterOp = languagesFilterOp;

    localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify(parsedNearbyCaregiverSettings));
    setLanguagesFilter(languagesFilter);
  };

  const handleSetLanguagesFilterOp = (languagesFilterOp) => {
    const normalizedLanguagesFilterOp = languagesFilterOp === 'AND' ? 'AND' : 'OR';
    const nearbyCaregiverSettings = localStorage.getItem('job/nearbyCaregiversSettings');
    const parsedNearbyCaregiverSettings = JSON.parse(nearbyCaregiverSettings);

    if (!nearbyCaregiverSettings || Object.keys(parsedNearbyCaregiverSettings).length === 0) {
      localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify({
        filters: [],
        languagesFilterOp: normalizedLanguagesFilterOp,
        radiusPreference: {},
      }));
      return setLanguagesFilterOp(normalizedLanguagesFilterOp);
    }

    parsedNearbyCaregiverSettings.languagesFilterOp = normalizedLanguagesFilterOp;
    localStorage.setItem('job/nearbyCaregiversSettings', JSON.stringify(parsedNearbyCaregiverSettings));
    setLanguagesFilterOp(normalizedLanguagesFilterOp);
  };

  // Load the data
  React.useEffect(() => {
    progressWrap(async () => {
      const result = await jobSource.api().getJob(jobRn, true, true);
      const applicants = result.applicants;
      const nearbyCaregiverSettings = localStorage.getItem('job/nearbyCaregiversSettings');
      const parsedNearbyCaregiverSettings = JSON.parse(nearbyCaregiverSettings);
      const radiusValue = parsedNearbyCaregiverSettings && parsedNearbyCaregiverSettings.radiusPreference[jobRn];

      if (radiusValue && Object.keys(!parsedNearbyCaregiverSettings).length === 0) {
        setRadius(radiusValue);
      }

      if (parsedNearbyCaregiverSettings) {
        const caregiverNameFilter = parsedNearbyCaregiverSettings.filters.find((filter) => filter.key === 'caregiverName');
        const caregiverStatusFilter = parsedNearbyCaregiverSettings.filters.find((filter) => filter.key === 'status');
        const languagesFilter = parsedNearbyCaregiverSettings.filters.find((filter) => filter.key === 'languages');
        setCaregiverNameFilter(caregiverNameFilter ? caregiverNameFilter.value : '');
        setCaregiverStatusFilter(caregiverStatusFilter ? caregiverStatusFilter.value : []);
        setLanguagesFilter(languagesFilter ? languagesFilter.value : []);
        setLanguagesFilterOp(parsedNearbyCaregiverSettings.languagesFilterOp === 'AND' ? 'AND' : 'OR');
      }

      setJob(result.job);
      setClient(result.client);
      setApplications(applicants);

      if (applicants.length > 0) {
        setFeedTab('INTERNAL_APPLICATIONS');
      } else {
        setFeedTab('INFORMATION');
      }

      setIsLoading(false);
    });
  }, [jobRn, progressWrap, refresh, jobSource]);
  React.useEffect(() => {
    setJobMessages({});
    setLatestAnnouncementMessage(null);
    setJobMessagesLastEvaluatedKey(null);
    jobBackend.load.loadMessages(jobRn, null);
  }, [jobRn, refresh]);

  React.useEffect(() => {
    if (jobData?.status === 'REVIEW') {
      // CQ-382 copies the read-only detail page only, so a REVIEW job still leaves for the
      // one edit route there is rather than a /career/loom path with nothing behind it.
      navigate(`/career/${jobRn}/edit`);
    }
  }, [jobData, navigate, jobRn]);

  const jobBackend = {
    load: {
      loadNearbyCaregivers: async (isCancelled=()=>false) => {
        progressWrap(async () => {
          setCaregiversLoading(true);
          try {
            const degreesToRadians = (degrees) => degrees * (Math.PI / 180);
            const distanceInKilometers = (origin, destination) => {
              const earthRadius = 6371;
              const latitudeDifference = degreesToRadians(destination.latitude - origin.latitude);
              const longitudeDifference = degreesToRadians(destination.longitude - origin.longitude);
              const originLatitude = degreesToRadians(origin.latitude);
              const destinationLatitude = degreesToRadians(destination.latitude);
              const haversine =
                Math.sin(latitudeDifference / 2) ** 2 +
                Math.cos(originLatitude) * Math.cos(destinationLatitude) *
                Math.sin(longitudeDifference / 2) ** 2;

              return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
            };
            const latitude = Number(jobLatitude);
            const longitude = Number(jobLongitude);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
              if (!isCancelled()) setCaregivers([]);
              return;
            }

            const jobCoordinates = {
              latitude,
              longitude,
            };
            const latitudeDelta = radius / 111.32;
            const longitudeDelta = radius / (111.32 * Math.cos(degreesToRadians(latitude)));
            const filters = [
              {
                key: 'status',
                value: caregiverStatusFilter.length > 0 && !debouncedCaregiverNameFilter ? caregiverStatusFilter : [
                  'ACTIVE',
                  'ENGAGED',
                  'PROFILE_PENDING',
                  'PROFILE_RESUME',
                  'INTERVIEW_REVIEW',
                  'PROFILE_REVIEW',
                ],
              },
            ];

            if (debouncedCaregiverNameFilter) {
              filters.push({
                key: 'caregiverName',
                value: `${debouncedCaregiverNameFilter}*`,
              });
            } else {
              filters.push(
                  {
                    key: 'latitude',
                    value: `[${latitude - latitudeDelta} TO ${latitude + latitudeDelta}]`,
                  },
                  {
                    key: 'longitude',
                    value: `[${longitude - longitudeDelta} TO ${longitude + longitudeDelta}]`,
                  },
              );
            }

            if (languagesFilter.length > 0 && !debouncedCaregiverNameFilter) {
              filters.push(
                  {
                    key: 'languages',
                    value: languagesFilter.map((language) => {
                      return `${languagesFilterOp === 'AND' ? '+' : ''}"${language}"`;
                    }).join(' '),
                  },
              );
            }

            const sort = {sortKey: 'createdAt', sortDirection: 'ASC'};
            const result = await backend.caregiver.queryCaregiver(filters, sort, null, {
              loadJobOutreach: true,
              jobRn,
            });
            const nearbyCaregivers = (result?.list || []).reduce((result, caregiver) => {
              const caregiverLatitude = Number(caregiver?.latitude);
              const caregiverLongitude = Number(caregiver?.longitude);

              if (!Number.isFinite(caregiverLatitude) || !Number.isFinite(caregiverLongitude)) {
                return result;
              }

              const distance = distanceInKilometers(jobCoordinates, {
                latitude: caregiverLatitude,
                longitude: caregiverLongitude,
              });

              if (distance <= radius || debouncedCaregiverNameFilter) {
                result.push({...caregiver, distance});
              }

              return result;
            }, []).sort((a, b) => a.distance - b.distance);

            if (!isCancelled()) setCaregivers(nearbyCaregivers);
          } finally {
            if (!isCancelled()) setCaregiversLoading(false);
          }
        }, {debugString: 'JOB_NEARBY_CAREGIVERS_LOAD'});
      },
      loadMessages: async (loadJobRn, lastEvaluatedKey) => {
        progressWrap(async () => {
          setJobMessagesLoading(true);
          const output = await backend.career.message.load(loadJobRn, lastEvaluatedKey, true, true);
          const outputMessages = output.messages;

          setJobMessages((messages) => {
            const newMessages = outputMessages.reduce((result, message) => {
              return {
                ...result,
                [message.messageRn]: message,
              };
            }, {});

            return {
              ...messages,
              ...newMessages,
            };
          });

          setLatestAnnouncementMessage((currentMessage) => {
            const latestLoadedAnnouncementMessage = outputMessages
                .filter((message) => message?.messageType === 'ANNOUNCEMENTS')
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;

            if (!latestLoadedAnnouncementMessage) {
              return currentMessage;
            }

            if (!currentMessage) {
              return latestLoadedAnnouncementMessage;
            }

            return new Date(latestLoadedAnnouncementMessage.createdAt).getTime() > new Date(currentMessage.createdAt).getTime() ?
              latestLoadedAnnouncementMessage :
              currentMessage;
          });

          setJobMessagesLastEvaluatedKey(output.lastEvaluatedKey);
          setJobMessagesLoading(false);
        }, {debugString: 'JOB_MESSAGE_LOAD'});
      },
    },
    job: {
      editStatus: async (status, onSuccess) => {
        progressWrap(async () => {
          const body = {
            status,
          };
          const result = await backend.career.updateJobStatus(jobRn, body);

          setJob((prev) => ({...prev, status: result.job.status}));

          onSuccess();

          return result;
        });
      },
      editPriority: async (priority, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              priority,
            };
            const result = await backend.career.updateJobPriority(jobRn, body);

            setJob((prev) => ({
              ...prev,
              priority: result.job.priority ?? null,
            }));

            onSuccess(result);

            return result;
          } catch (error) {
            onError(error);
            console.error(error);
            throw new Error(error);
          }
        });
      },
    },
    applications: {
      denyApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'REJECTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
      startAccept: (application) => {
        progressWrap(async () => {
          // Load the caregiver's bio (for the dialog's profile-completeness check) and status (for warnings).
          const caregiver = await backend.caregiver.getCaregiver(application.caregiverRn, undefined, true);
          const prefilledCaregiver = {
            caregiverRn: application.caregiverRn,
            caregiverName: application.caregiver.name,
            bio: caregiver?.caregiverBio,
          };
          const warnings = {
            caregiverNotActive: caregiver?.caregiverDetail?.status !== 'ACTIVE',
            noRegisteredClient: !clientRn,
          };

          if (warnings.caregiverNotActive || warnings.noRegisteredClient) {
            setAcceptWarning({application, prefilledCaregiver, warnings});
          } else {
            setAcceptTarget({application, prefilledCaregiver});
          }
        }, {debugString: 'JOB_START_ACCEPT'});
      },
      proceedAccept: (application, prefilledCaregiver) => {
        setAcceptWarning(null);
        if (clientRn) {
          setAcceptTarget({application, prefilledCaregiver});
          return;
        }
        // No registered client to attach a roster entry to — just mark the application accepted.
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'ACCEPTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
        }, {debugString: 'JOB_ACCEPT_ONLY'});
      },
      acceptAndAddToRoster: async (application, caregiverData) => {
        progressWrap(async () => {
          const {
            caregiverRn,
            status,
            recommendation,
            languageChips,
            recommendationChips,
            availabilityOverlap,
            availability,
            availabilityLastUpdatedTime,
          } = caregiverData;

          if (recommendation) {
            await backend.caregiver.addRecommendation(caregiverRn, {
              recommendationId: clientRn,
              recommendation: {blob: recommendation, editorType: 'MARKDOWN'},
              languageChips,
              recommendationChips,
              availabilityOverlap,
              availability,
              availabilityLastUpdatedTime,
              overwrite: true,
            });
          }

          await backend.client.addCaregiver(clientRn, {caregiverRn, status});

          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'ACCEPTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));

          setAcceptTarget(null);
        }, {debugString: 'JOB_ACCEPT_AND_ADD_TO_ROSTER'});
      },
    },
    messages: {
      create: async (messageType, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
              messageType,
              requester: {
                rn: requesterRn,
              },
            };
            const result = await backend.career.message.create(jobRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (result.message.messageType === 'ANNOUNCEMENTS') {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_CREATE'});
      },
      update: async (messageRn, newMessage, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              body: {
                blob: newMessage,
                editorType: 'MARKDOWN',
              },
            };
            const result = await backend.career.message.update(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_UPDATE'});
      },
      delete: async (messageRn, onSuccess, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            await backend.career.message.delete(jobRn, messageRn);

            setJobMessages((messages) => {
              const nextMessages = {...messages};
              delete nextMessages[messageRn];
              return nextMessages;
            });
            if (latestAnnouncementMessage?.messageRn === messageRn) {
              setLatestAnnouncementMessage(null);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_DELETE'});
      },
      addEmoji: async (messageRn, emoji, emojiRequesterRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji,
              requester: {
                rn: emojiRequesterRn,
              },
            };
            const result = await backend.career.message.emoji.add(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_EMOJI_ADD'});
      },
      removeEmoji: async (messageRn, emoji, emojiRequesterRn, onSuccess=()=>undefined, onError=()=>undefined) => {
        progressWrap(async () => {
          try {
            const body = {
              emoji,
              requester: {
                rn: emojiRequesterRn,
              },
            };
            const result = await backend.career.message.emoji.remove(jobRn, messageRn, body);

            setJobMessages((messages) => ({
              ...messages,
              [result.messageRn]: result.message,
            }));
            if (latestAnnouncementMessage?.messageRn === result.messageRn) {
              setLatestAnnouncementMessage(result.message);
            }

            onSuccess();
          } catch (error) {
            onError();
            console.error(error);
            throw new Error(error);
          }
        }, {debugString: 'JOB_MESSAGE_EMOJI_REMOVE'});
      },
    },
  };

  React.useEffect(() => {
    if (isLoading) return;

    let cancelled = false;
    jobBackend.load.loadNearbyCaregivers(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [caregiverStatusFilter, debouncedCaregiverNameFilter, isLoading, jobLatitude, jobLongitude, languagesFilter, languagesFilterOp,
    progressWrap, radius]);

  if (isLoading) return null;

  return (
    <Box
      sx={{
        textAlign: 'left',
        width: {
          xs: '100%',
          md: '75%',
        },
      }}
    >
      <JobFeed
        showJobTypePill={jobSource.showJobTypePill}
        jobRn={jobRn}
        requester={requester}
        jobData={jobData}
        applications={applications}
        client={client}
        caregivers={caregivers}
        caregiversLoading={caregiversLoading}
        caregiverNameFilter={caregiverNameFilter}
        setCaregiverNameFilter={setCaregiverNameFilter}
        caregiverStatusFilter={caregiverStatusFilter}
        setCaregiverStatusFilter={handleSetCaregiverStatusFilter}
        languagesFilter={languagesFilter}
        setLanguagesFilter={handleSetLanguagesFilter}
        languagesFilterOp={languagesFilterOp}
        setLanguagesFilterOp={handleSetLanguagesFilterOp}
        radius={radius}
        setRadius={handleSetRadius}
        jobBackend={jobBackend}
        jobMessages={jobMessages}
        latestAnnouncementMessage={latestAnnouncementMessage}
        jobMessagesLastEvaluatedKey={jobMessagesLastEvaluatedKey}
        jobMessagesLoading={jobMessagesLoading}
        feedTab={feedTab}
        setFeedTab={setFeedTab}
        refresh={refresh}
        setRefresh={setRefresh}
      />
      {
        acceptTarget &&
        <AddCaregiver
          title={translate('career.jobDetail.AcceptAndAddToRosterTitle', {
            clientName: clientName || translate('career.jobDetail.ThisClient'),
          })}
          submitLabel={translate('career.jobDetail.AcceptAndAdd')}
          prefilledCaregiver={acceptTarget.prefilledCaregiver}
          clientRn={clientRn}
          statusProgressWrap={progressWrap}
          onClose={() => setAcceptTarget(null)}
          addCaregiver={(caregiverData) => jobBackend.applications.acceptAndAddToRoster(acceptTarget.application, caregiverData)}
        />
      }
      {
        acceptWarning &&
        <Dialog open={true} onClose={() => setAcceptWarning(null)}>
          <DialogTitle onClose={() => setAcceptWarning(null)}>
            {translate('career.jobDetail.AcceptWarning.Title')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{my: 1}}>
              {
                acceptWarning.warnings.caregiverNotActive &&
                <Typography variant="body2">
                  {`• ${translate('career.jobDetail.AcceptWarning.CaregiverNotActive')}`}
                </Typography>
              }
              {
                acceptWarning.warnings.noRegisteredClient &&
                <Typography variant="body2">
                  {`• ${translate('career.jobDetail.AcceptWarning.NoRegisteredClient')}`}
                </Typography>
              }
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setAcceptWarning(null)}>
              {translate('generic.Cancel')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => jobBackend.applications.proceedAccept(acceptWarning.application, acceptWarning.prefilledCaregiver)}
            >
              {translate('career.jobDetail.AcceptWarning.AcceptAnyways')}
            </Button>
          </DialogActions>
        </Dialog>
      }
    </Box>
  );
};

JobView.propTypes = {
  jobRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
  jobSource: PropTypes.object,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(JobView));
